import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { AdminBadgeDto, CampaignDto } from '@/api/recognition';
import { StepInstructions } from './StepInstructions';
import { StepUploadFile } from './StepUploadFile';
import { StepReview } from './StepReview';
import { StepConfirm } from './StepConfirm';
import type { WizardStep } from './types';

interface Props {
  onClose: () => void;
  /** Campaña elegida — se muestra y se puede cambiar DENTRO del asistente (Step 1), ver
   *  `onCampaignChange`. Solo aplica al modo "vigente"; el modo "histórica" no usa campaña. */
  campaignId: string;
  /** Catálogo completo, para pintar el selector de campaña dentro del asistente sin volver a
   *  pedirlo: el padre (`AdminWinners`) ya lo tiene cargado para su propia barra de herramientas. */
  campaigns: CampaignDto[];
  onCampaignChange: (campaignId: string) => void;
  campaignBadges: AdminBadgeDto[];
}

const STEPS: WizardStep[] = ['instructions', 'upload', 'review', 'confirm'];
const STEP_LABEL_KEYS: Record<WizardStep, string> = {
  instructions: 'bulk.stepInstructions',
  upload: 'bulk.stepUpload',
  review: 'bulk.stepReview',
  confirm: 'bulk.stepConfirm',
};

/**
 * Asistente de carga del padrón de Ganadores (D-10/Fase 3), reemplazo del modal de un solo paso
 * `WinnerUploadModal`. Mismo patrón de 4 pasos que `bulkUpload/BulkUploadModal` (Instrucciones →
 * Subir → Revisar → Confirmar): validación previa (`preview: true`) antes de comprometer nada, y
 * un resultado final persistente en vez del panel de 8 s que había antes.
 */
export function WinnersImportWizardModal({
  onClose, campaignId, campaigns, onCampaignChange, campaignBadges,
}: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const [step, setStep] = useState<WizardStep>('instructions');
  const [modo, setModo] = useState<'vigente' | 'historica'>('vigente');
  const [badgeId, setBadgeId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const stepIndex = STEPS.indexOf(step);

  // `createPortal` a `document.body`: este asistente se monta dentro de `AdminSection`
  // (`.adm-section`), que lleva `backdrop-filter` — por especificación CSS eso convierte a esa
  // sección en el "containing block" de cualquier descendiente `position: fixed`. Sin portal,
  // `.modal-overlay` (`inset: 0`) quedaba encajado en el tamaño de `.adm-section` en vez de cubrir
  // la ventana completa: en la pantalla de Ganadores (tabla larga + filtros) el modal terminaba
  // centrado muy por debajo del scroll actual y el usuario tenía que desplazar la página para
  // encontrarlo. Mismo fix ya aplicado en `ConfirmModal` y `PostItDetailModal.tsx`.
  return createPortal((
    <div className="modal-overlay modal-overlay--fade" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bulk-modal bulk-modal--wizard" onClick={(event) => event.stopPropagation()}>
        <button className="rec-modal-close" onClick={onClose} aria-label={t('common.close')}><i className="fas fa-times" /></button>
        <h2 className="bulk-title"><i className="fas fa-trophy" /> {t('admin.winners.uploadModalTitle')}</h2>
        <p className="bulk-sub">{t('admin.winners.importWizard.subtitle')}</p>

        <div className="bulk-stepper" role="tablist" aria-label={t('admin.winners.uploadModalTitle')}>
          {STEPS.map((wizardStep, index) => (
            <div
              key={wizardStep}
              className={`bulk-stepper-item ${index === stepIndex ? 'bulk-stepper-item--on' : ''} ${index < stepIndex ? 'bulk-stepper-item--done' : ''}`}
            >
              <span className="bulk-stepper-num">{index + 1}</span>
              <span className="bulk-stepper-label">{t(STEP_LABEL_KEYS[wizardStep])}</span>
            </div>
          ))}
        </div>
        <p className="bulk-step-of">{t('bulk.stepOf', { step: stepIndex + 1, total: STEPS.length })}</p>

        {step === 'instructions' && (
          <StepInstructions
            modo={modo} onModoChange={setModo}
            campaignId={campaignId}
            campaigns={campaigns} onCampaignChange={onCampaignChange}
            badgeId={badgeId} onBadgeIdChange={setBadgeId}
            campaignBadges={campaignBadges}
            onNext={() => setStep('upload')}
            onClose={onClose}
          />
        )}
        {step === 'upload' && (
          <StepUploadFile
            onBack={() => setStep('instructions')}
            onFileSelected={(selected) => { setFile(selected); setStep('review'); }}
          />
        )}
        {step === 'review' && file && (
          <StepReview
            file={file} modo={modo} campaignId={campaignId} badgeId={badgeId}
            onBack={() => setStep('upload')}
            onNext={() => setStep('confirm')}
          />
        )}
        {step === 'confirm' && file && (
          <StepConfirm
            file={file} modo={modo} campaignId={campaignId} badgeId={badgeId}
            onDone={onClose}
          />
        )}
      </div>
    </div>
  ), document.body);
}

export default WinnersImportWizardModal;
